import dayjs from 'dayjs';
import type {
  SupportAttachmentType,
  SupportAuthorSide,
  SupportTicketStatus,
} from '../types/supportTicket';
import {
  SUPPORT_MAX_IMAGE_BYTES,
  SUPPORT_MAX_VIDEO_BYTES,
  SUPPORT_MEDIA_RETENTION_DAYS,
} from '../types/supportTicket';

export function computeMediaPurgeAt(resolvedAt: Date, retentionDays = SUPPORT_MEDIA_RETENTION_DAYS): Date {
  return dayjs(resolvedAt).add(retentionDays, 'day').toDate();
}

export function nextStatusAfterMessage(
  currentStatus: SupportTicketStatus,
  authorSide: SupportAuthorSide
): SupportTicketStatus {
  if (currentStatus === 'resolved') {
    return 'resolved';
  }
  return authorSide === 'nauvatek' ? 'waiting_client' : 'open';
}

export function canClientReply(status: SupportTicketStatus): boolean {
  return status !== 'resolved';
}

export function canNauvatekClose(status: SupportTicketStatus): boolean {
  return status !== 'resolved';
}

export function previewFromBody(body: string, hasAttachments: boolean, maxLen = 120): string {
  const trimmed = body.trim().replace(/\s+/g, ' ');
  if (trimmed) {
    return trimmed.length > maxLen ? `${trimmed.slice(0, maxLen - 1)}…` : trimmed;
  }
  return hasAttachments ? 'Pièce jointe' : '';
}

export function resolveAttachmentType(contentType: string, fileName: string): SupportAttachmentType | null {
  const lowerType = (contentType || '').toLowerCase();
  const lowerName = (fileName || '').toLowerCase();
  if (lowerType.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic)$/i.test(lowerName)) {
    return 'image';
  }
  if (lowerType.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(lowerName)) {
    return 'video';
  }
  return null;
}

export function validateSupportAttachment(file: {
  type: string;
  name: string;
  size: number;
}): { ok: true; attachmentType: SupportAttachmentType } | { ok: false; error: string } {
  const attachmentType = resolveAttachmentType(file.type, file.name);
  if (!attachmentType) {
    return { ok: false, error: 'Seules les images et vidéos sont acceptées.' };
  }
  const max = attachmentType === 'image' ? SUPPORT_MAX_IMAGE_BYTES : SUPPORT_MAX_VIDEO_BYTES;
  if (file.size > max) {
    const maxMo = Math.round(max / (1024 * 1024));
    return {
      ok: false,
      error:
        attachmentType === 'image'
          ? `Image trop lourde (max ${maxMo} Mo).`
          : `Vidéo trop lourde (max ${maxMo} Mo).`,
    };
  }
  return { ok: true, attachmentType };
}

export function isTicketVisibleToClient(ticket: { createdByUserId: string }, userId: string): boolean {
  return ticket.createdByUserId === userId;
}

export function isTicketVisibleToNauvatekStaff(args: {
  isSuperAdmin: boolean;
  isCSM: boolean;
  businessIds: string[] | undefined;
  ticketBusinessId: string;
}): boolean {
  if (args.isSuperAdmin) return true;
  if (!args.isCSM) return false;
  return (args.businessIds ?? []).includes(args.ticketBusinessId);
}

/** Messages support non lus par le client (dernier msg NauvaTek après dernier read). */
export function hasUnreadSupportReply(ticket: {
  lastMessageAuthorSide?: SupportAuthorSide | string;
  lastMessageAt: Date;
  clientLastReadAt?: Date;
}): boolean {
  if (ticket.lastMessageAuthorSide !== 'nauvatek') return false;
  if (!ticket.clientLastReadAt) return true;
  return ticket.lastMessageAt.getTime() > ticket.clientLastReadAt.getTime();
}

/** Messages client non lus par NauvaTek (dernier msg client après dernier read staff). */
export function hasUnreadClientReply(ticket: {
  lastMessageAuthorSide?: SupportAuthorSide | string;
  lastMessageAt: Date;
  staffLastReadAt?: Date;
  status?: string;
}): boolean {
  if (ticket.status === 'resolved') return false;
  if (ticket.lastMessageAuthorSide !== 'client') return false;
  if (!ticket.staffLastReadAt) return true;
  return ticket.lastMessageAt.getTime() > ticket.staffLastReadAt.getTime();
}
